import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Activity, buildTranslator, CustomForm, CustomFormAnswer } from '../../../../../amorg-common';
import { getActivity } from '../../../../../api/activityClientApi';
import { customFormsVideoSubmissionEnabled, getCustomForm, getCustomFormAnswer } from '../../../../../api/customFormClientApi';
import { getMaybeActiveUserOrRefresh } from '../../../../../api/serverApiHelpers';
import { PageHeader } from '../../../../../designSystem/PageHeader';
import { Button } from '../../../../../designSystem/button/Button';
import { ToolBarButton, ToolBarContainer } from '../../../../../designSystem/compositeComponents/ButtonToolBar';
import { SingleColumnLayout } from '../../../../_layouts/SingleColumnLayout';
import { P } from '../../../../../designSystem/typography/Body';
import { clientAccessHelper } from '../../../../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../../../../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../../../../_pageWrappers/MemberPageWrapper';
import { CustomFormAnswerSubmissionPanel } from '../../../_components/CustomFormAnswerSubmissionPanel';
import { PrintCustomFormAnswerPage } from './PrintCustomFormAnswerPage';


export function CustomFormAnswerPage():JSX.Element
{
    const { formId = '' } = useParams<{formId:string, answerId:string}>();
    const [selectedMenuItemKey, setSelectedMenuItemKey] = React.useState('');
    const [crumbState, setCrumbState] = React.useState<{activityTitle:string, formName:string, activityId:string} | null>(null);

    React.useEffect(() =>
    {
        const load = async () =>
        {
            const form = await getCustomForm(formId);
            setSelectedMenuItemKey(form?.category || '');
        };
        load();
    }, [formId]);

    return (
        <MemberPageWrapper
            selectedMenuItemKey={selectedMenuItemKey}
            breadCrumbs={[
                {text:'Ergonomiske undersøgelser', url:'/ergonomics'},
                ...(crumbState ? [
                    {text:crumbState.activityTitle, url:`/customforms/${crumbState.activityId}`},
                    {text:crumbState.formName, url:''}
                ] : [])
            ]}>
            <CustomFormAnswerPageContent onCrumbStateChanged={setCrumbState} />
        </MemberPageWrapper>
    );
}

type CustomFormAnswerPageContentProps = {
    onCrumbStateChanged:(state:{activityTitle:string, formName:string, activityId:string} | null) => void,
}

function CustomFormAnswerPageContent(props:CustomFormAnswerPageContentProps):JSX.Element
{
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewCustomFormAnswerList']);

    const { formId = '', answerId = '' } = useParams<{formId:string, answerId:string}>();

    const [state, setState] = React.useState<{
        loading:boolean,
        selectedMenuItemKey:string,
        siteId:string,
        form:CustomForm | null,
        activity:Activity | null
    }>({
        loading:true,
        selectedMenuItemKey:'',
        siteId:'',
        form:null,
        activity:null
    });

    
    React.useEffect(()=>{
        const getData = async ()=>{
            const user = await getMaybeActiveUserOrRefresh(['ViewCustomFormAnswerList']);
            const form = await getCustomForm(formId);
            if(!user.tokenUser || !form)
                return;

            const activity = await getActivity(form.activityId);
            setState({form, activity, siteId:user.tokenUser.siteId, selectedMenuItemKey:form.category, loading:false});
        };
        getData();
    }, [formId, answerId]);

    const [isLocked, setIsLocked] = React.useState(true);
    const [inPrint, setInPrint] = React.useState(false);

    const [printState, setPrintState] = React.useState<{answer:CustomFormAnswer, videoSubmissionEnabled:boolean} | null>(null);

    const translator = buildTranslator('da');

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        
        contentRef: printRef,
        documentTitle: state.form ? translator(state.form.name) : '',
        onAfterPrint: () => setInPrint(false)
    });

    async function handlePrint(){
        const answer = await getCustomFormAnswer(answerId, state.siteId);
        if(!answer)
            return;

        const videoSubmissionEnabled = await customFormsVideoSubmissionEnabled(state.siteId);
        setPrintState({ answer, videoSubmissionEnabled });
        setInPrint(true);
        printFn();
    }

    React.useEffect(() =>
    {
        props.onCrumbStateChanged(state.form && state.activity
            ? { activityTitle: state.activity.title, formName: translator(state.form.name), activityId: state.form.activityId }
            : null);
    }, [state.form, state.activity]);

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return (
        
        <>
            <PageHeader 
                headerText="Formular" 
                headerStyle="secondary-gray"
                narrow
                buttons={
                
                <ToolBarContainer>
                    {isLocked && 'Lås op'}
                    <Button 
                        icon={isLocked ? 'lock' : 'lock_open'}
                        toolTipText={isLocked ? 'Lås skema op' : 'Lås skema'}
                        onClick={() => setIsLocked(!isLocked)} 
                        variant={isLocked ? 'destructive-text' : 'text'} />

                    <ToolBarButton
                        icon='interface_actions_print'
                        tooltip='Print'
                        variant='text'
                        onClick={handlePrint}
                        />

                </ToolBarContainer> }
                />

            <SingleColumnLayout narrow>
                <>

                {state.form && inPrint && printState &&
                    <PrintCustomFormAnswerPage
                        form={state.form}
                        siteId={state.siteId}
                        answer={printState.answer}
                        videoSubmissionEnabled={printState.videoSubmissionEnabled}
                        innerRef={printRef} />        
                }


                {state.form &&
                    <CustomFormAnswerSubmissionPanel siteId={state.siteId} form={state.form} answerId={answerId} disableAnswerComponents={isLocked} />
                }
                </>
            </SingleColumnLayout>
            
        </>
    );
}